import {
  IUserTokenData,
  IExternalAccount,
  CommonLocale,
  LanguageEnum,
  CountryEnum,
  IGroup,
  Permission,
  MogiriSettingSupplier,
} from './types';
import request from '@/utils/request';
import { sleep } from '@/utils/index';

// request & response
export interface ILoginRequest {
  supplierId: string;
  username: string;
  password: string;
}

export interface ILoginResponse {
  token: IUserTokenData;
}

export interface IUpdateAccountPasswordRequest {
  password: string;
}

export interface IUpdateAccountPasswordResponse {
  version?: string;
}

export interface IListAccountsRequest {
  page?: number;
  itemCount?: number;
}

export interface IListAccountsResponse {
  accounts: IExternalAccount[];
  totalCount?: number;
  page?: number;
  itemCount?: number;
}

export interface IGetAccountRequest {
  accountId: string;
}

export interface IPutAccountRequest {
  id?: string;
  username: string;
  password: string;
  groupId: string;
  locale: CommonLocale;
  fullName: string;
  isInactive: boolean;
  version?: string;
}

export interface IAddDelegatedAccountsRequest {
  accountId: string;
  delegatedAccounts: IAddDelegatedAccountsRequest.DelegatedAccountInfo[];
}

export namespace IAddDelegatedAccountsRequest {
  export interface DelegatedAccountInfo {
    supplierId: string;
    username: string;
    password: string;
  }
}

export interface IRemoveDelegatedAccountsRequest {
  accountId: string;
  delegatedAccounts: IRemoveDelegatedAccountsRequest.DelegatedAccountInfo[];
}

export namespace IRemoveDelegatedAccountsRequest {
  export interface DelegatedAccountInfo {
    supplierId: string;
    accountId: string;
  }
}

export interface IListGroupsRequest {
  page?: number;
  itemCount?: number;
}

export interface IListGroupsResponse {
  groups: IGroup[];
  totalCount?: number;
  page?: number;
  itemCount?: number;
}

export interface IGetGroupRequest {
  groupId?: string;
}

export interface IGetGroupResponse {
  group: IGroup;
}

export interface IPutGroupRequest {
  id?: string;
  supplierId: string;
  name: string;
  permissions: Permission[];
  isInactive: boolean;
  version?: string;
}

export interface ExternalSupplierSetting {
  supplierId?: string;
  useReversiblePassword?: boolean;
  allowUserPasswordChanging?: boolean;
  // passwordRequirment?: SupplierSettingPasswordRequirement;
  // timeZone?: string;
  // operationPolicies?: SupplierSettingOperationPolicy[];
  mogiriSetting?: MogiriSettingSupplier;
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';

const mockUserTokenData = (req: ILoginRequest) => {
  const current = new Date();
  const expired = new Date(current);
  expired.setDate(current.getDate() + 1);
  return {
    accountId: `id_${req.username}`,
    username: req.username,
    issuedTime: Math.floor(current.getTime() / 1000).toString(),
    expiredTime: Math.floor(expired.getTime() / 1000).toString(),
    value: `dummyToken-${req.password}`,
    supplierId: req.supplierId,
    groupId: 'dummyGroupId',
    groupName: 'dummyGroupName',
    displayName: req.username,
    version: '0',
  } as IUserTokenData;
};

const mockListAccounts = (req: IListAccountsRequest) => {
  return {
    accounts: [mockAccount(1), mockAccount(2)],
  } as IListAccountsResponse;
};

const mockAccount = (num: number) =>
  ({
    id: 'id' + num,
    username: 'username' + num,
    locale: {
      language: LanguageEnum.JAPANESE,
      country: CountryEnum.UNIVERSAL,
    },
    fullName: 'FullName ' + num,
    decryptedPassword: '',
    groupId: 'groupId' + num,
    delegatedAccounts: [],
    isInactive: false,
    version: '0',
  } as IExternalAccount);

// api
export const login = async (req: ILoginRequest) => {
  if (useMock) {
    await sleep(1000);
    return mockUserTokenData(req);
  }

  const res = await request({
    url: 'v1/login',
    method: 'post',
    data: req,
  });
  return (res.data as ILoginResponse).token;
};

export const logout = async (req: any) => {
  await sleep(500);
  return;
};

export const updateAccountPassword = async (
  req: IUpdateAccountPasswordRequest
) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/update-account-password',
    method: 'post',
    data: req,
  });
  return res.data as IUpdateAccountPasswordResponse;
};

export const listAccounts = async (req: IListAccountsRequest) => {
  if (useMock) {
    await sleep(1000);
    return mockListAccounts(req);
  }

  const res = await request({
    url: 'v1/list-accounts',
    method: 'post',
    data: req,
  });
  return res.data as IListAccountsResponse;
};

export const getAccount = async (req: IGetAccountRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/get-account',
    method: 'post',
    data: req,
  });
  return res.data as IExternalAccount;
};

export const putAccount = async (req: IPutAccountRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/put-account',
    method: 'post',
    data: req,
  });
  return res.data as IExternalAccount;
};

export const addDelegatedAccounts = async (
  req: IAddDelegatedAccountsRequest
) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/add-delegated-accounts',
    method: 'post',
    data: req,
  });
  return res.data as IExternalAccount;
};

export const removeDelegatedAccounts = async (
  req: IRemoveDelegatedAccountsRequest
) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/remove-delegated-accounts',
    method: 'post',
    data: req,
  });
  return res.data as IExternalAccount;
};

export const listGroups = async (req: IListGroupsRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/list-groups',
    method: 'post',
    data: req,
  });
  return res.data as IListGroupsResponse;
};

export const getGroup = async (req: IGetGroupRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/get-group',
    method: 'post',
    data: req,
  });
  return res.data as IGetGroupResponse;
};

export const putGroup = async (req: IPutGroupRequest) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/put-group',
    method: 'post',
    data: req,
  });
  return res.data as IGroup;
};

export const getSetting = async () => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/get-supplier-setting',
    method: 'post',
  });
  return res.data as ExternalSupplierSetting;
};

export const putMogiriSetting = async (req: MogiriSettingSupplier) => {
  if (useMock) {
    throw new Error('Not implemented');
  }

  const res = await request({
    url: 'v1/put-supplier-mogiri-setting',
    method: 'post',
    data: req,
  });
  return res.data as ExternalSupplierSetting;
};
